import clsx from 'clsx';
import { atom, useAtom } from 'jotai';
import { atomFamily } from 'jotai-family';
import * as React from 'react';

import type * as api from '@gitbook/api-client';
import { uploadFiles as uploadFilesToLocation } from '@gitbook/app-database';
import { isDocumentEmpty, trimDocument } from '@gitbook/doc-core';
import { PaletteButton, type PaletteDef } from '@gitbook/react-uikit';
import type { Change, Document, Value } from '@gitbook/slate';
import { Icon } from '@gitbook/spine-icons';
import { Button, toast } from '@gitbook/spine-react';
import {
    DEFAULT_CONTENT_TYPE,
    getContentTypeFromPath,
    getOpenAISupportedFileContentType,
    isImportableContentType,
    isOpenAISupportedFile,
    MAX_IMPORT_FILE_SIZE,
    normalizeContentType,
    ZIP_MIME_TYPES,
} from '@gitbook/util-files';
import { type LoadingStateHookResult, useEventCallback } from '@gitbook/util-react-hooks';
import { assertIsDefined, filterOutNullable } from '@gitbook/util-ts';

import { useCurrentOrganizationId } from '../../state';
import { promptFile } from '../../utils';
import {
    type CompiledPromptTemplate,
    compilePromptTemplates,
    GLOBAL_PROMPT_TEMPLATES,
    MENU_PROMPT_LIST,
    PAGE_PROMPT_TEMPLATES,
    SELECTION_MENU_PROMPT_LIST,
    useAgentEnabled,
    useAgentInputPrompt,
} from '../Agent';
import {
    BasicEditor,
    type BasicEditorProps,
    type BasicEditorRef,
    InsertPageMentionButton,
    useBasicEditorLocalStorageValue,
} from '../BasicEditor';
import { useOptionalContentReferenceContext } from '../ContentReferences';
import { uploadOrganizationFiles } from '../Files';
import { AddContentMenu } from './AddContentMenu';
import { AttachmentsSummary } from './AttachmentsSummary';

const spaceMentions: BasicEditorProps['mentions'] = {
    '#': ['page'],
    '@': ['page'],
};

const noMentions: BasicEditorProps['mentions'] = {};

/**
 * Image mime-types supported as attachments for the agent. These are sent as image inputs; every
 * other supported type goes through the generic OpenAI file inputs.
 */
const AGENT_SUPPORTED_IMAGE_MIME_TYPES = new Set([
    'image/jpeg',
    'image/png',
    'image/gif',
    'image/webp',
]);

/**
 * The content type of a file the agent can import but not read, or `null` for any other file.
 * A ZIP archive is that case: the import unpacks it, no model reads it.
 */
function getImportOnlyFileContentType(file: { name: string; type?: string | null }): string | null {
    const contentType = (() => {
        if (file.type) {
            try {
                return normalizeContentType(file.type);
            } catch {
                // Ignore an unparseable content type and fall back to the extension.
            }
        }

        return getContentTypeFromPath(file.name);
    })();

    return ZIP_MIME_TYPES.includes(contentType) ? contentType : null;
}

/**
 * Whether a picked or dropped file can be attached to the agent: a supported image, any type
 * accepted as an OpenAI file input (matched on its mime type or its extension), or a file the
 * agent can import without reading.
 */
function isAgentSupportedFile(file: File): boolean {
    return (
        AGENT_SUPPORTED_IMAGE_MIME_TYPES.has(file.type) ||
        isOpenAISupportedFile(file) ||
        getImportOnlyFileContentType(file) !== null
    );
}

/**
 * Whether the content import can read a file: an archive, or a document it parses.
 */
function isImportableFile(file: File): boolean {
    return (
        getImportOnlyFileContentType(file) !== null ||
        isImportableContentType(getOpenAISupportedFileContentType(file) ?? '')
    );
}

/**
 * A file attachment that has been added to the input but not yet uploaded.
 * The underlying file is kept locally (with an object URL used for previewing)
 * and is only uploaded to the space when the message is submitted.
 */
export type AgentChatLocalAttachment = {
    /** Stable local id used as a React key and to remove the attachment. */
    id: string;
    /** The local file that will be uploaded when the message is submitted. */
    file: File;
    /** Object URL used to preview the attachment before it is uploaded. */
    previewURL: string;
    /** Whether the attachment is an image, a file the agent reads, or one it can only import. */
    type: 'image' | 'file' | 'import-file';
};

export type AgentChatInputValue = {
    content: Value;
    attachments: AgentChatLocalAttachment[];
};

export type SetAgentChatInputValue = (value: {
    content?: React.SetStateAction<Value>;
    attachments?: React.SetStateAction<AgentChatLocalAttachment[]>;
}) => void;

const agentChatInputAttachmentsAtom = atomFamily((_key: string) =>
    atom<AgentChatLocalAttachment[]>([])
);

let localAttachmentIdCounter = 0;

/**
 * Create a local attachment from a file, generating an object URL for previewing it.
 */
function createLocalAttachment(file: File): AgentChatLocalAttachment {
    return {
        id: `agent-attachment-${localAttachmentIdCounter++}`,
        file,
        previewURL: URL.createObjectURL(file),
        type: AGENT_SUPPORTED_IMAGE_MIME_TYPES.has(file.type)
            ? 'image'
            : getImportOnlyFileContentType(file)
              ? 'import-file'
              : 'file',
    };
}

/**
 * Convert a local attachment into an API attachment shape used for previewing it
 * in the attachments list (using the local object URL as the source).
 * The `url` points to a local object URL, so this should only be used for display
 * or heuristics, never to send the attachment to the backend.
 */
export function localAgentAttachmentToPreview(
    attachment: AgentChatLocalAttachment
): api.AIAttachment {
    if (attachment.type === 'image') {
        return {
            type: 'image',
            url: attachment.previewURL,
            filename: attachment.file.name,
        } satisfies api.AIAttachmentImage;
    }

    if (attachment.type === 'import-file') {
        return {
            type: 'import-file',
            url: attachment.previewURL,
            filename: attachment.file.name,
            contentType: getImportOnlyFileContentType(attachment.file) ?? DEFAULT_CONTENT_TYPE,
        } satisfies api.AIAttachmentImportFile;
    }

    return {
        type: 'file',
        url: attachment.previewURL,
        filename: attachment.file.name,
        contentType: getOpenAISupportedFileContentType(attachment.file) ?? DEFAULT_CONTENT_TYPE,
    } satisfies api.AIAttachmentFile;
}

/**
 * Break down the attachments of a message for analytics, counted per kind: a file attached only to
 * be imported never reaches the model, so counting it as a file input would overstate them.
 *
 * Takes the kinds alone, so it counts a message's attachments whether they are still local or
 * already uploaded.
 */
export function countAgentChatAttachments(
    attachments: Array<{ type: api.AIAttachment['type'] }> | undefined
): {
    attachmentsCount: number;
    imageAttachmentsCount: number;
    fileAttachmentsCount: number;
    importFileAttachmentsCount: number;
} {
    const countOfType = (type: api.AIAttachment['type']) =>
        (attachments ?? []).filter((attachment) => attachment.type === type).length;

    return {
        attachmentsCount: attachments?.length ?? 0,
        imageAttachmentsCount: countOfType('image'),
        fileAttachmentsCount: countOfType('file'),
        importFileAttachmentsCount: countOfType('import-file'),
    };
}

/**
 * Where the attachments of a conversation are uploaded.
 *
 * A conversation held inside a space keeps uploading there; one held against an organization — the
 * agent dock, which runs in the application frame with no content context — uploads to the
 * organization's file storage instead.
 */
export type AgentChatUploadTarget =
    | { kind: 'space'; spaceId: string }
    | { kind: 'organization'; organizationId: string };

/**
 * A file uploaded for a conversation, in the shape the attachments are derived from.
 */
type UploadedAttachment = {
    url: string;
    name: string;
    contentType: string;
};

/**
 * Hook returning a function that uploads local attachments to the given target and
 * converts them into API attachments ready to be sent with a message.
 * Returns an empty array when there is nothing to upload.
 *
 * @param target - Where to upload the attachments to.
 */
export function useUploadAgentChatAttachments(target: AgentChatUploadTarget | undefined) {
    return useEventCallback(
        async (attachments: AgentChatLocalAttachment[]): Promise<api.AIAttachment[]> => {
            if (attachments.length === 0) {
                return [];
            }

            assertIsDefined(target);

            const uploaded = await uploadAgentChatFiles(target, attachments);

            return uploaded
                .map((metadata, index) => {
                    if (AGENT_SUPPORTED_IMAGE_MIME_TYPES.has(metadata.contentType)) {
                        return {
                            type: 'image',
                            url: metadata.url,
                            filename: metadata.name,
                        } satisfies api.AIAttachmentImage;
                    }

                    const file = attachments[index]?.file;

                    // Attached for the agent to import, never for a model to read.
                    const importContentType = file ? getImportOnlyFileContentType(file) : null;
                    if (importContentType) {
                        return {
                            type: 'import-file',
                            url: metadata.url,
                            filename: metadata.name,
                            contentType: importContentType,
                        } satisfies api.AIAttachmentImportFile;
                    }

                    // Resolve from the original file so code/text files whose browser mime type is
                    // empty still get a type OpenAI accepts, derived from the extension.
                    const contentType = getOpenAISupportedFileContentType({
                        name: file?.name ?? metadata.name,
                        type: metadata.contentType,
                    });
                    if (contentType) {
                        return {
                            type: 'file',
                            url: metadata.url,
                            filename: metadata.name,
                            contentType,
                        } satisfies api.AIAttachmentFile;
                    }

                    return null;
                })
                .filter(filterOutNullable);
        }
    );
}

/**
 * Payload generated by a prompt template for the agent chat.
 */
export type AgentChatPromptPayload = {
    content: string | Document;
    attachments?: api.AIAttachment[];
};

/**
 * Prompt template result describing whether it should populate the input or send immediately.
 */
export type AgentChatCompiledPrompt =
    | {
          id: string;
          kind: 'draft';
          payload: {
              content: string;
              attachments?: api.AIAttachment[];
          };
      }
    | {
          id: string;
          kind: 'submit';
          payload: AgentChatPromptPayload;
      };

/**
 * Callback to create a prompt templates palette for an agent chat input.
 */
export type AgentChatInputPromptTemplatePalette = (
    implementPrompt: (input: AgentChatCompiledPrompt) => void
) => PaletteDef;

/**
 * Hook to get a value for the agent chat input.
 */
export function useAgentChatInputValue(persistedKey: string) {
    const {
        value: content,
        setValue: setContent,
        resetValue: resetContent,
    } = useBasicEditorLocalStorageValue(persistedKey);

    const [attachments, setAttachments] = useAtom(agentChatInputAttachmentsAtom(persistedKey));

    const value = React.useMemo(() => {
        return {
            content,
            attachments,
        };
    }, [content, attachments]);

    const setValue = React.useCallback<SetAgentChatInputValue>(
        (value) => {
            if (value.content) {
                setContent(value.content);
            }
            const attachmentsInput = value.attachments;
            if (attachmentsInput) {
                setAttachments((prevAttachments) => {
                    const nextAttachments =
                        typeof attachmentsInput === 'function'
                            ? attachmentsInput(prevAttachments)
                            : attachmentsInput;
                    const nextAttachmentIds = new Set(
                        nextAttachments.map((attachment) => attachment.id)
                    );
                    prevAttachments.forEach((attachment) => {
                        if (!nextAttachmentIds.has(attachment.id)) {
                            URL.revokeObjectURL(attachment.previewURL);
                        }
                    });
                    return nextAttachments;
                });
            }
        },
        [setContent, setAttachments]
    );

    const resetValue = React.useCallback(() => {
        resetContent();
        setAttachments((prevAttachments) => {
            prevAttachments.forEach((attachment) => URL.revokeObjectURL(attachment.previewURL));
            return [];
        });
    }, [resetContent, setAttachments]);

    return { value, setValue, resetValue };
}

/**
 * File handling shared by the full-chat drop zone and its composer controls.
 */
export function useAgentChatFiles(setValue: SetAgentChatInputValue) {
    const { space } = useOptionalContentReferenceContext() ?? {};
    const organizationId = useCurrentOrganizationId();
    const uploadTarget = React.useMemo((): AgentChatUploadTarget | undefined => {
        if (space) {
            return { kind: 'space', spaceId: space };
        }
        return organizationId ? { kind: 'organization', organizationId } : undefined;
    }, [space, organizationId]);

    const onAddFiles = useEventCallback((files: File[]) => {
        const validFiles: File[] = [];
        files.forEach((file) => {
            if (!isAgentSupportedFile(file)) {
                toast.error(
                    `The file ${file.name} (${file.type || 'unknown type'}) is not supported`,
                    null,
                    {
                        description: `You can attach images, PDFs, documents, spreadsheets, text or code files, and ZIP archives to import.`,
                    }
                );
                return;
            }

            // Bounded for any file the import reads: the import is what enforces the limit, and it
            // would only report it minutes later.
            if (isImportableFile(file) && file.size > MAX_IMPORT_FILE_SIZE) {
                toast.error(
                    `The file ${file.name} is too large (maximum ${MAX_IMPORT_FILE_SIZE / (1024 * 1024)}MB)`,
                    null
                );
                return;
            }

            validFiles.push(file);
        });

        if (validFiles.length === 0) {
            return;
        }

        setValue({
            attachments: (prevAttachments) => [
                ...prevAttachments,
                ...validFiles.map(createLocalAttachment),
            ],
            content: (prevContent) => prevContent.change().focus().value,
        });
    });

    return { uploadTarget, onAddFiles };
}

/**
 * Input to post a new message in the chat.
 */
export function AgentChatInput(props: {
    inputKey: string;
    value: AgentChatInputValue;
    setValue: SetAgentChatInputValue;
    uploadTarget: AgentChatUploadTarget | undefined;
    onAddFiles: (files: File[]) => void;

    /**
     * Whether a text selection is part of the message context. Switches the prompt templates to
     * their selection-scoped wording; the surface renders the selection itself.
     */
    hasSelection?: boolean;

    /**
     * Whether the input is editable.
     */
    editable?: boolean;

    /**
     * Whether the input is submittable.
     */
    submittable?: boolean;

    /**
     * Placeholder for the input.
     */
    placeholder?: string;

    /**
     * Callback to submit the input.
     * The local attachments are uploaded before this is called, so it receives the
     * uploaded API attachments ready to be sent with the message.
     */
    onSubmit?: (attachments: api.AIAttachment[]) => void;

    /**
     * Callback when picking a template prompt.
     */
    onPickTemplatePrompt?: (input: { id: string }) => void;

    /**
     * Callback when a template prompt should be submitted immediately.
     */
    onSubmitTemplatePrompt?: (input: AgentChatCompiledPrompt) => void;

    /**
     * Callback to stop the processing of the current input.
     * When provided, it'll render instead of the submit button.
     */
    onStop?: () => void;

    /**
     * If true, the input is displayed in a floating panel style, with different padding and input styles.
     */
    floating?: boolean;

    /**
     * Controls rendered in the toolbar, beside the attach and mention controls. For context the
     * surface offers rather than the message carries (see `inputToolbarActions` on the chat).
     */
    toolbarActions?: React.ReactNode;
    /**
     * Whether a submission is uploading its attachments, owned by the chat so it can also stop
     * files dropped or pasted around the composer while the draft is about to be cleared.
     */
    submittingState: LoadingStateHookResult;
}) {
    const {
        value,
        uploadTarget,
        onAddFiles,
        editable = true,
        submittable = true,
        setValue,
        onSubmit,
        onStop,
        onPickTemplatePrompt,
        onSubmitTemplatePrompt,
        placeholder = 'Ask GitBook Agent to do anything',
        floating = false,
        inputKey,
        hasSelection = false,
        toolbarActions,
        submittingState,
    } = props;

    const agentEnabled = useAgentEnabled();
    const editorRef = React.useRef<BasicEditorRef>(null);
    const [isSubmitting, wrapSubmitting] = submittingState;
    const { space } = useOptionalContentReferenceContext() ?? {};
    const uploadAttachments = useUploadAgentChatAttachments(uploadTarget);
    const isEmpty = React.useMemo(() => {
        return isDocumentEmpty(trimDocument(value.content.document));
    }, [value.content.document]);

    const onChange = (change: Change) => {
        setValue({ content: change.value });
    };

    const onPickPromptTemplate = useEventCallback((input: { id: string }) => {
        onPickTemplatePrompt?.(input);
    });

    const promptTemplates = React.useMemo(() => {
        if (!agentEnabled) {
            return [...PAGE_PROMPT_TEMPLATES, ...GLOBAL_PROMPT_TEMPLATES];
        }

        if (!hasSelection) {
            return [...MENU_PROMPT_LIST];
        }

        // With a selection attached, the same prompts have to act on it rather than on the whole
        // page — otherwise picking "Make it shorter" rewrites everything while the composer shows
        // only a selection.
        const selectionPromptsById = new Map(
            SELECTION_MENU_PROMPT_LIST.flatMap((group) => group.prompts).map((prompt) => [
                prompt.id,
                prompt,
            ])
        );

        return MENU_PROMPT_LIST.map((group) => ({
            ...group,
            prompts: group.prompts.map((prompt) => selectionPromptsById.get(prompt.id) ?? prompt),
        }));
    }, [agentEnabled, hasSelection]);

    const setInputWithPrompt = useAgentInputPrompt({
        inputKey,
        editable,
    });

    const onHandleCompiledPrompt = useEventCallback((template: CompiledPromptTemplate) => {
        const compiledPrompt: AgentChatCompiledPrompt = {
            id: template.id,
            kind: template.immediate ? 'submit' : 'draft',
            payload: {
                content: template.prompt,
            },
        };

        onPickPromptTemplate({ id: template.id });

        if (compiledPrompt.kind === 'submit') {
            onSubmitTemplatePrompt?.(compiledPrompt);
            return;
        }

        setInputWithPrompt(compiledPrompt.payload.content);
    });

    const defaultPromptTemplatePalette = useEventCallback(() =>
        compilePromptTemplates(promptTemplates, null, onHandleCompiledPrompt)
    );

    const examplesPalette = useEventCallback(() => defaultPromptTemplatePalette());

    const mentions = React.useMemo<BasicEditorProps['mentions']>(() => {
        return {
            ...(space ? spaceMentions : noMentions),
            ...(agentEnabled
                ? {
                      '/': {
                          getPalette: () => defaultPromptTemplatePalette(),
                      },
                  }
                : {}),
        };
    }, [agentEnabled, defaultPromptTemplatePalette, space]);

    /**
     * Prompt the user to select files and add them as local attachments.
     */
    const onPickFiles = async () => {
        const files = await promptFile({ accept: 'files', multiple: true });
        if (files.length > 0) {
            onAddFiles(files);
        }
    };

    /**
     * Upload the local attachments and submit the message.
     * While uploading, the input behaves as if the agent was already thinking.
     */
    const handleSubmit = useEventCallback(() => {
        if (isPostDisabled) {
            return;
        }

        void wrapSubmitting(async () => {
            const uploaded = await toast.catch(
                () => uploadAttachments(value.attachments),
                'Error while uploading files'
            );

            // Keep the input intact so the user can retry when the upload failed.
            if (!uploaded) {
                return;
            }

            onSubmit?.(uploaded);
        }).then(() => {
            // Hand focus back: submitting locks the editor read-only for the upload, and the lock
            // takes the caret with it — without this, sending leaves the composer defocused and a
            // follow-up message starts with a click instead of typing.
            setValue({
                content: (prevContent) => prevContent.change().focus().value,
            });
        });
    });

    // Focus the composer on mount, placing the caret at the end of any existing draft.
    React.useEffect(() => {
        setValue({
            content: (prevContent) =>
                prevContent.change().collapseToEndOf(prevContent.document).focus().value,
        });
    }, [setValue]);

    // While the agent waits for an answer to its question there is no upload target, so files can neither be added nor sent.
    const uploadsDisabledReason = uploadTarget
        ? undefined
        : "Files can't be added while the agent waits for your answer.";
    const hasUnsendableFiles = !uploadTarget && value.attachments.length > 0;
    const isPostDisabled =
        (isEmpty && value.attachments.length === 0) ||
        !editable ||
        !submittable ||
        isSubmitting ||
        hasUnsendableFiles;
    // While uploading the attachments on submit, lock the input and prevent editing
    // (including removing attachments) so the message can't be mutated mid-upload.
    const isInputEditable = editable && !isSubmitting;
    return (
        <div
            className={clsx(
                'flex cursor-text flex-col border-0.5 bg-base transition',
                // Floating surfaces sit on the page rather than in it, so the box carries a
                // light shadow of its own to lift it off whatever is scrolling behind.
                floating ? 'rounded-xl p-2 shadow-subtle' : 'rounded-lg',
                // Focus shows as a border change rather than a ring: the composer is a large
                // box, and a ring around the whole thing reads as an alert.
                value.content.isFocused ? 'border-neutral-focus' : 'border-base'
            )}
        >
            <div
                className="flex flex-col gap-3"
                onClick={(event) => {
                    event.stopPropagation();
                    onChange(value.content.change().focus());
                }}
            >
                <AttachedContextList
                    editable={isInputEditable}
                    value={value}
                    setValue={setValue}
                    floating={floating}
                />
                <BasicEditor
                    ref={editorRef}
                    value={value.content}
                    onChange={onChange}
                    readOnly={!isInputEditable}
                    placeholder={placeholder}
                    className={clsx(
                        '[&>[contenteditable]]:max-h-[40dvh] [&>[contenteditable]]:overflow-y-auto',
                        floating ? 'p-0' : 'py-3 [&>div]:px-4'
                    )}
                    mentions={mentions}
                    onSubmit={submittable && onSubmit ? handleSubmit : undefined}
                    onPasteFiles={isInputEditable && uploadTarget ? onAddFiles : undefined}
                />
                <div
                    className={clsx(
                        'flex justify-between gap-3',
                        floating ? 'items-center' : 'px-3 pb-2'
                    )}
                >
                    {/* Grouped so what the surface adds keeps its own tight spacing beside the
                        composer's own controls rather than inheriting the row's. */}
                    <div className="flex items-center gap-1">
                        {space ? (
                            <AdditionalContentToolbar
                                onPickFiles={onPickFiles}
                                uploadsDisabledReason={uploadsDisabledReason}
                                examplesPalette={examplesPalette}
                                setInputWithPrompt={setInputWithPrompt}
                                editable={isInputEditable}
                                setValue={setValue}
                            />
                        ) : (
                            // Outside a space the rest of the toolbar has nothing to work from — no
                            // pages to mention, no content examples — so only attaching is offered.
                            <AttachFilesButton
                                editable={isInputEditable}
                                onPickFiles={onPickFiles}
                                disabledReason={uploadsDisabledReason}
                            />
                        )}
                        {toolbarActions}
                    </div>
                    {onSubmit ? (
                        // `ml-auto` rather than relying on `justify-between`: the row's leading
                        // controls are conditional, and without them a lone submit button would be
                        // pushed to the left of the row.
                        <div className="ml-auto flex items-center gap-2">
                            {onStop ? (
                                <Button
                                    size="small"
                                    kind="blank"
                                    label="Stop"
                                    iconOnly
                                    leadingIcon={Icon.Stop}
                                    onPress={onStop}
                                />
                            ) : null}
                            {/* Beside the stop control once a draft (text or files) exists: posting mid-response
                                stacks the message for when the agent finishes, and taking the
                                composer away would make writing ahead impossible. */}
                            {!onStop || !isEmpty || value.attachments.length > 0 ? (
                                <Button
                                    size="small"
                                    kind="secondary"
                                    label={onStop ? 'Queue message' : 'Post'}
                                    tooltip={
                                        hasUnsendableFiles
                                            ? {
                                                  tooltip:
                                                      "Files can't be sent while the agent waits for your answer. Remove them to send.",
                                              }
                                            : {
                                                  tooltip: onStop
                                                      ? 'Sent when the agent finishes'
                                                      : 'Type a message',
                                                  disabled: !onStop && !isPostDisabled,
                                              }
                                    }
                                    iconOnly={agentEnabled}
                                    leadingIcon={agentEnabled ? Icon.ArrowUp : undefined}
                                    disabled={isPostDisabled}
                                    loadable={submittingState}
                                    onPress={handleSubmit}
                                />
                            ) : null}
                        </div>
                    ) : null}
                </div>
            </div>
        </div>
    );
}

/**
 * Attach control on its own, for a conversation held outside a space.
 */
function AttachFilesButton(props: {
    editable: boolean;
    onPickFiles: () => void;
    disabledReason?: string;
}) {
    const { editable, onPickFiles, disabledReason } = props;

    return (
        <Button
            kind="blank"
            size="small"
            leadingIcon={Icon.Paperclip}
            iconOnly
            label="Attach files"
            title={disabledReason}
            disabled={!editable || Boolean(disabledReason)}
            onPress={() => {
                void onPickFiles();
            }}
        />
    );
}

function AdditionalContentToolbar(props: {
    onPickFiles: () => void;
    uploadsDisabledReason?: string;
    examplesPalette: () => PaletteDef;
    setInputWithPrompt: (prompt: string) => void;
    editable: boolean;
    setValue: SetAgentChatInputValue;
}) {
    const {
        onPickFiles,
        uploadsDisabledReason,
        examplesPalette,
        setInputWithPrompt,
        editable,
        setValue,
    } = props;
    const agentEnabled = useAgentEnabled();
    return agentEnabled ? (
        <div className="flex gap-1">
            <AddContentMenu
                onPickFiles={() => {
                    void onPickFiles();
                }}
                uploadsDisabledReason={uploadsDisabledReason}
                onTriggerSlashCommands={() => setInputWithPrompt('/')}
            />
            <InsertPageMentionButton
                readOnly={!editable}
                setValue={(content) => setValue({ content })}
                leadingIcon={Icon.AtSign}
                size="small"
            />
        </div>
    ) : (
        <div className="flex">
            <InsertPageMentionButton
                readOnly={!editable}
                setValue={(content) => setValue({ content })}
            />
            <Button
                kind="blank"
                leadingIcon={Icon.Paperclip}
                iconOnly
                label="Attach files (Images or PDF)"
                title={uploadsDisabledReason}
                disabled={!editable || Boolean(uploadsDisabledReason)}
                onPress={() => {
                    void onPickFiles();
                }}
            />
            <PaletteButton
                size="medium"
                kind="blank"
                label="Examples"
                leadingIcon={Icon.AIImproveWriting}
                trailingIcon={Icon.ChevronDown}
                iconOnly
                palette={examplesPalette}
            />
        </div>
    );
}

function AttachedContextList(props: {
    editable: boolean;
    value: AgentChatInputValue;
    setValue: SetAgentChatInputValue;
    floating?: boolean;
}) {
    const { editable, setValue, value, floating } = props;
    const attachments = value.attachments;
    const previewAttachments = React.useMemo(
        () => attachments.map(localAgentAttachmentToPreview),
        [attachments]
    );
    const onRemoveAttachment = useEventCallback((index: number) => {
        setValue({
            attachments: (prevAttachments) => {
                const removed = prevAttachments[index];
                if (removed) {
                    URL.revokeObjectURL(removed.previewURL);
                }
                return prevAttachments.filter((_, i) => i !== index);
            },
        });
    });
    return previewAttachments.length > 0 ? (
        // Wraps rather than scrolls: the composer is narrow, and a horizontal strip hides context
        // the user is being told is attached — the one thing these chips exist to make visible.
        <div className={clsx('flex flex-wrap gap-1.5', floating ? 'px-0' : 'px-3 pt-3')}>
            <AttachmentsSummary
                wraps
                attachments={previewAttachments}
                onRemoveAttachment={editable ? onRemoveAttachment : undefined}
            />
        </div>
    ) : null;
}

/**
 * Upload the files of a conversation, wherever that conversation stores them.
 */
async function uploadAgentChatFiles(
    target: AgentChatUploadTarget,
    attachments: AgentChatLocalAttachment[]
): Promise<UploadedAttachment[]> {
    const files = attachments.map((attachment) => attachment.file);

    if (target.kind === 'organization') {
        const uploaded = await uploadOrganizationFiles({
            organizationId: target.organizationId,
            // The browser reports no type for many code and text files, so the type the file is
            // stored as is the one resolved from its name, matching what is sent to the model.
            files: attachments.map((attachment) => {
                const contentType =
                    getImportOnlyFileContentType(attachment.file) ??
                    getOpenAISupportedFileContentType(attachment.file) ??
                    attachment.file.type;
                return { file: attachment.file, ...(contentType ? { contentType } : {}) };
            }),
        });

        return uploaded.map((file) => ({
            url: file.url,
            name: file.filename,
            contentType: file.contentType,
        }));
    }

    const uploaded = await uploadFilesToLocation(
        {
            kind: 'space_file',
            space: target.spaceId,
        },
        files
    );

    return uploaded.map((metadata) => ({
        url: metadata.downloadURL,
        name: metadata.name,
        contentType: metadata.contentType,
    }));
}
